<template>
  <div class="navbar app-topbar">
    <!-- Left: hidden screen selector kept for routing logic -->
    <div class="topbar-screen-selector dropdown dropright">
      <div
        class="dropdown-toggle screen-pill"
        type="button"
        data-toggle="dropdown"
        id="dropdownMenuButton1"
      >
        <span class="screen-pill__dot"></span>
        {{ selectedScreenName || 'Select Screen' }}
        <i class="pi pi-chevron-down"></i>
      </div>
      <div class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
        <template v-bind:key="index" v-for="(screen, index) in screens">
          <a v-on:click="selectScreen(screen)" class="dropdown-item">
            {{ screen.name }}
          </a>
        </template>
      </div>
    </div>

    <!-- Right: KodeAI, notification, refresh, support, user pill -->
    <div class="topbar-actions">
      <kode-ai-button class="topbar-kode" />
      <button class="topbar-icon-btn" title="Notifications">
        <i class="pi pi-bell"></i>
      </button>
      <button class="topbar-icon-btn" title="Refresh" @click="getScreens">
        <font-awesome-icon icon="fa-solid fa-arrows-rotate" />
      </button>
      <router-link
        :to="{ name: TICKETS_ROUTE }"
        class="topbar-icon-btn topbar-support-link"
        :title="supportTitle"
        :aria-label="supportTitle"
      >
        <i class="pi pi-info-circle"></i>
        <span v-if="unreadTickets" class="tk-badge topbar-badge">{{ unreadTickets > 99 ? '99+' : unreadTickets }}</span>
      </router-link>

      <div class="dropdown dropleft">
        <button
          class="user-pill dropdown-toggle"
          type="button"
          data-toggle="dropdown"
          id="dropdownMenuButton2"
        >
          <span class="user-pill__avatar">
            {{ initials }}
          </span>
          <span class="user-pill__name">{{ displayName }}</span>
        </button>
        <div class="dropdown-menu" aria-labelledby="dropdownMenuButton2">
          <router-link :to="{ name: PROFILE_ROUTE }" class="dropdown-item">
            Profile
          </router-link>
          <router-link
            v-if="ADMIN_ROLES.includes(account.role)"
            :to="{ name: DONATION_SETTINGS_ROUTE }"
            class="dropdown-item"
          >
            Settings
          </router-link>
          <a v-on:click="logout()" class="dropdown-item final-item text-danger"
            >Log out</a
          >
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { PROFILE_ROUTE, DONATION_SETTINGS_ROUTE, TICKETS_ROUTE } from '@/router/routes';
import { GET_SCREENS, LOGOUT } from '@/store/action_types';
import { ADMIN_ROLES } from '@/utils/const';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
library.add(
  faArrowsRotate,
);
export default {
  data() {
    return {
      PROFILE_ROUTE,
      DONATION_SETTINGS_ROUTE,
      TICKETS_ROUTE,
      ADMIN_ROLES,
      selectedScreenName: null,
      selectedScreenId: null,
    };
  },
  computed: {
    route() {
      return this.$route.name;
    },
    screens() {
      return this.$store.getters.screens.data;
    },
    screensIsLoading() {
      return this.$store.getters.screens.isLoading;
    },
    // Tickets with unread replies — the badge on the support link.
    unreadTickets() {
      return Number(this.$store.getters.ticketUnread?.data?.tickets) || 0;
    },
    supportTitle() {
      if (!this.unreadTickets) return 'Support tickets';
      return `Support tickets — ${this.unreadTickets} with unread replies`;
    },
    account() {
      return this.$store.getters.account.data;
    },
    displayName() {
      const a = this.account || {};
      const full = [a.fname, a.lname].filter(Boolean).join(' ').trim();
      return full || a.email || 'Account';
    },
    initials() {
      const a = this.account || {};
      const f = (a.fname || '').trim();
      const l = (a.lname || '').trim();
      if (f || l) return ((f[0] || '') + (l[0] || '')).toUpperCase();
      const e = (a.email || '').trim();
      return e ? e.slice(0, 2).toUpperCase() : '··';
    },
  },
  methods: {
    selectScreen(screen) {
      this.selectedScreenName = screen.name;
      this.selectedScreenId = screen.id;
      this.$router.replace({
        query: {
          ...this.$route.query,
          screenId: screen.id
        }
      });
    },
    getScreens() {
      this.$store.dispatch(GET_SCREENS, { organizationId: this.account.organizationId }).then((res) => {
        if (res && res.length > 0) {
          this.selectedScreenName = res[0].name;
          this.selectedScreenId = res[0].id;
        }
      });
    },
    logout() {
      this.$store.dispatch(LOGOUT).then((res) => {
        this.$router.go();
      });
    },
  },
  mounted() {
    this.getScreens()
  }
};
</script>
<style scoped>
.app-topbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 4rem;
  background-color: var(--color-bg-canvas);
  border-bottom: 1px solid var(--color-border);
  padding: 0 1.25rem;
}

/* Hidden screen selector — kept so router-driven screen logic still works,
   but hidden from view to match the top bar design. */
.topbar-screen-selector {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  pointer-events: none;
  opacity: 0;
}

/* ---- Right actions ---- */
/* "Ask KodeAI" leads the actions, a little apart from the icons. */
.topbar-kode {
  margin-right: 6px;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.topbar-icon-btn {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease;
}

.topbar-icon-btn:hover {
  background-color: var(--color-bg-muted);
  color: var(--color-primary-hover);
  border-color: var(--color-border);
}

.topbar-icon-btn .pi {
  font-size: 1rem;
}

.topbar-support-link {
  position: relative;
  text-decoration: none !important;
}

.topbar-badge {
  position: absolute;
  top: 2px;
  right: 1px;
  box-shadow: 0 0 0 2px var(--color-bg-canvas);
}

/* ---- User pill ---- */
.user-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 3px 12px 3px 4px;
  background-color: var(--color-bg-canvas);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color .15s ease, box-shadow .15s ease, border-color .15s ease;
  margin-left: 4px;
}

.user-pill:hover {
  border-color: var(--color-primary-border);
  background-color: var(--color-bg-soft);
  box-shadow: var(--shadow-xs);
}

.user-pill__avatar {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: var(--color-text-on-primary);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.user-pill__name {
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-pill__chev {
  font-size: 10px;
  color: var(--color-text-muted);
  margin-left: 2px;
}

/* Drop the default Bootstrap caret triangle */
.user-pill.dropdown-toggle::after,
.user-pill.dropdown-toggle::before {
  display: none !important;
}

/* Screen pill (used in dropdown trigger / list rows globally) */
.screen-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background-color: var(--color-bg-canvas);
  font-size: 0.78rem;
  color: var(--color-text-primary);
  cursor: pointer;
}

.screen-pill__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--color-success);
}

.dropdown {
  position: relative;
}

.dropdown .pi-chevron-down {
  font-size: 11px;
}
</style>
